import { all, takeEvery, takeLatest, put, cancelled } from 'redux-saga/effects';
import siteConfig from '@iso/config/site.config';
import { notification } from '@iso/components';
import { safe } from '../utils/sagaHelpers';
import actions from './actions';
const gaxios = require('gaxios');
gaxios.instance.defaults = {
    baseURL: siteConfig.siteUrl.API_URL
};

const BASE = 'api/contabilidad/conciliacion';

// Leídos en cada request: si se leen al cargar el módulo, un login posterior deja el token vacío.
const authHeaders = () => ({
    Authorization: localStorage.getItem('access_token'),
    AuthId: localStorage.getItem('id_token'),
});

// Multipart: gaxios serializa `data` a JSON, así que el FormData va en `body` y
// fetch pone solo el Content-Type con el boundary.
const postMultipart = (url, formData) => gaxios.request({
    url,
    method: 'POST',
    body: formData,
    headers: authHeaders(),
});

// Garantiza que el _ERROR se despache en cualquier salida que no sea el camino feliz
// (success:false, 4xx/5xx, error de red). `cuerpo` devuelve true solo si terminó bien.
// `safe()` atrapa la excepción para notificar; el finally corre antes de que salga de aquí.
// Si la tarea fue cancelada por takeLatest no se despacha el _ERROR: la request nueva
// ya puso su loading en true y apagarlo aquí mostraría el empty-state antes de tiempo.
function* conError(tipoError, cuerpo) {
    let terminadoOk = false;
    try {
        terminadoOk = yield* cuerpo();
    } finally {
        if (!terminadoOk && !(yield cancelled())) yield put({ type: tipoError });
    }
}

const falloRespuesta = (res) => {
    notification('error', res.data?.msg || 'Error desconocido');
    return false;
};

export function* previsualizar(action) {
    yield* conError(actions.CONC_PREVISUALIZAR_ERROR, function* () {
        const data = new FormData();
        data.append('file', action.file);
        data.append('banco', action.banco);
        data.append('tipo', action.tipo);
        if (action.forzarMapeo) data.append('forzarMapeo', 'true');
        if (action.anioReferencia) data.append('anioReferencia', String(action.anioReferencia));
        if (action.filaEncabezado !== undefined && action.filaEncabezado !== null) {
            data.append('filaEncabezado', String(action.filaEncabezado));
        }

        const res = yield postMultipart(`${BASE}/previsualizar`, data);
        if (!res.data.success) return falloRespuesta(res);

        if (res.data.aviso) notification('warning', res.data.aviso);
        yield put({ type: actions.CONC_PREVISUALIZAR_SUCCESS, payload: res.data });
        return true;
    });
}

export function* guardarMapeo(action) {
    yield* conError(actions.CONC_GUARDAR_MAPEO_ERROR, function* () {
        const res = yield gaxios.request({
            url: `${BASE}/mapeo`,
            method: 'POST',
            data: action.mapeo,
            headers: authHeaders(),
        });
        if (!res.data.success) return falloRespuesta(res);

        notification('success', 'Mapeo guardado. Se usará automáticamente en las próximas cargas de este banco.');
        yield put({ type: actions.CONC_GUARDAR_MAPEO_SUCCESS });
        yield put(actions.previsualizar({ banco: action.mapeo.banco, tipo: action.mapeo.tipo, file: action.file }));
        return true;
    });
}

export function* getSugerencias(action) {
    yield* conError(actions.CONC_SUGERENCIAS_ERROR, function* () {
        const res = yield gaxios.request({
            url: `${BASE}/sugerencias`,
            method: 'POST',
            data: { banco: action.banco, tipo: action.tipo, filas: action.filas },
            headers: authHeaders(),
        });
        if (!res.data.success) return falloRespuesta(res);

        yield put({
            type: actions.CONC_SUGERENCIAS_SUCCESS,
            payload: { resultados: res.data.resultados, ventanaDias: res.data.ventanaDias },
        });
        return true;
    });
}

export function* confirmar(action) {
    yield* conError(actions.CONC_CONFIRMAR_ERROR, function* () {
        const res = yield gaxios.request({
            url: `${BASE}/confirmar`,
            method: 'POST',
            data: {
                tipo: action.tipo,
                registroId: action.candidato._id,
                referenciaBancaria: action.referenciaBancaria,
                fechaBanco: action.fechaBanco,
            },
            headers: authHeaders(),
        });
        if (!res.data.success) return falloRespuesta(res);

        notification('success', `Registro #${action.candidato.codigo ?? ''} conciliado`);
        yield put({
            type: actions.CONC_CONFIRMAR_SUCCESS,
            payload: {
                indice: action.indice,
                confirmacion: {
                    registroId: action.candidato._id,
                    referenciaBancaria: action.referenciaBancaria,
                    candidato: action.candidato,
                },
            },
        });
        return true;
    });
}

export function* desconciliar(action) {
    yield* conError(actions.CONC_DESCONCILIAR_ERROR, function* () {
        const res = yield gaxios.request({
            url: `${BASE}/desconciliar/${action.tipo}/${action.registroId}`,
            method: 'PUT',
            headers: authHeaders(),
        });
        if (!res.data.success) return falloRespuesta(res);

        notification('success', 'Conciliación revertida');
        yield put({ type: actions.CONC_DESCONCILIAR_SUCCESS, payload: { indice: action.indice } });
        return true;
    });
}

export function* registrarCarga(action) {
    yield* conError(actions.CONC_REGISTRAR_CARGA_ERROR, function* () {
        const { resumen } = action;
        const data = new FormData();
        data.append('file', action.file);
        data.append('banco', resumen.banco);
        data.append('tipo', resumen.tipo);
        if (resumen.desde) data.append('desde', resumen.desde);
        if (resumen.hasta) data.append('hasta', resumen.hasta);
        data.append('totalFilasArchivo', String(resumen.totalFilasArchivo));
        data.append('totalConfirmadas', String(resumen.totalConfirmadas));
        data.append('totalSinCoincidencia', String(resumen.totalSinCoincidencia));
        data.append('filasSinCoincidencia', JSON.stringify(resumen.filasSinCoincidencia || []));

        const res = yield postMultipart(`${BASE}/cargas`, data);
        if (!res.data.success) return falloRespuesta(res);

        notification('success', 'Carga registrada en el histórico');
        yield put({ type: actions.CONC_REGISTRAR_CARGA_SUCCESS, payload: res.data.carga });
        yield put(actions.getCargas('currentPage=1&pageSize=10'));
        return true;
    });
}

export function* getCargas(action) {
    yield* conError(actions.CONC_GET_CARGAS_ERROR, function* () {
        const query = action.query ? `?${action.query}` : '';
        const res = yield gaxios.request({
            url: `${BASE}/cargas${query}`,
            headers: authHeaders(),
        });
        if (!res.data.success) return falloRespuesta(res);

        yield put({
            type: actions.CONC_GET_CARGAS_SUCCESS,
            payload: { cargas: res.data.cargas, paginationInfo: res.data.paginationInfo },
        });
        return true;
    });
}

export default function* rootSaga() {
    yield all([
        takeLatest(actions.CONC_PREVISUALIZAR_REQUEST, safe(previsualizar)),
        takeEvery(actions.CONC_GUARDAR_MAPEO_REQUEST, safe(guardarMapeo)),
        takeLatest(actions.CONC_SUGERENCIAS_REQUEST, safe(getSugerencias)),
        takeEvery(actions.CONC_CONFIRMAR_REQUEST, safe(confirmar)),
        takeEvery(actions.CONC_DESCONCILIAR_REQUEST, safe(desconciliar)),
        takeEvery(actions.CONC_REGISTRAR_CARGA_REQUEST, safe(registrarCarga)),
        takeLatest(actions.CONC_GET_CARGAS_REQUEST, safe(getCargas)),
    ]);
}
